import { parse, parseISO, isValid, format } from 'date-fns'

const DISPLAY_FORMAT = 'dd.MM.yyyy'

/**
 * Допустимые символы для ввода даты: цифры, точка, пробел, дефис (для диапазона).
 * Остальное удаляется.
 */
export function sanitizeDateString(str) {
  if (str == null || typeof str !== 'string') return ''
  return str.replace(/[^\d.\s-]/g, '')
}

/**
 * Нормализует ввод вида "11112026" или "111126" в "11.11.2026" или "11.11.26".
 * - 8 цифр подряд: ddmmyyyy -> dd.mm.yyyy
 * - 6 цифр: ddmmyy -> dd.mm.yy (двузначный год)
 * - Уже с точками не трогаем (только trim).
 */
export function normalizeToDotted(str) {
  const s = String(str).trim()
  const digitsOnly = s.replace(/\D/g, '')
  if (digitsOnly.length === 8) {
    return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 4)}.${digitsOnly.slice(4, 8)}`
  }
  if (digitsOnly.length === 6) {
    return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 4)}.${digitsOnly.slice(4, 6)}`
  }
  return s
}

/** Строка похожа на ISO дату (с бэка приходят такие после сохранения). */
function isIsoLike(str) {
  return /^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(String(str).trim())
}

/**
 * Парсит одну строку даты (для одного поля или одной части диапазона).
 * Принимает ISO с бэка ("2026-02-11", "2026-02-11T00:00:00.000Z"), "11.11.2026", "11112026", "11.11.26".
 * Не бросает исключений — при любой ошибке возвращает null (избегаем RangeError: Invalid time value).
 * @returns {Date|null}
 */
export function parseDateInput(value) {
  try {
    if (value == null || typeof value !== 'string') return null
    const s = String(value).trim()
    if (!s) return null
    // Сначала пробуем ISO (ответ API после сохранения), иначе поля дат в карточках очищаются
    if (isIsoLike(s)) {
      try {
        const d = parseISO(s)
        if (d && isValid(d) && !Number.isNaN(d.getTime())) return d
      } catch {
        // fallback к форматам с точками
      }
    }
    const sanitized = sanitizeDateString(value).trim()
    if (!sanitized) return null
    const normalized = normalizeToDotted(sanitized)
    if (!normalized) return null

    const formats = [DISPLAY_FORMAT, 'd.M.yyyy', 'dd.M.yyyy', 'd.MM.yyyy', 'd.M.yy', 'dd.MM.yy']
    for (const fmt of formats) {
      try {
        const d = parse(normalized, fmt, new Date())
        if (d && isValid(d) && !Number.isNaN(d.getTime())) return d
      } catch {
        continue
      }
    }
  } catch {
    // любая ошибка (в т.ч. RangeError) — не пробрасываем
  }
  return null
}

/**
 * Парсит период: "11.11.2026 - 20.11.2026", "11.11.2026-20.11.2026".
 * Для одной даты возвращает Date; для периода — [Date, Date]; иначе null.
 * @returns {Date|[Date, Date]|null}
 */
export function parseDateRangeInput(value) {
  try {
    if (value == null) return null
    if (value instanceof Date) {
      return isValid(value) && !Number.isNaN(value.getTime()) ? value : null
    }
    if (Array.isArray(value)) {
      const a = value.map(v => (v instanceof Date ? v : parseDateInput(String(v ?? ''))))
      if (a[0] instanceof Date && a[1] instanceof Date) return [a[0], a[1]]
      if (a[0] instanceof Date) return [a[0], a[0]]
      return null
    }
    const s = String(value).trim()
    if (!s) return null

    // Период с разделителем " - " или "-"
    const m = s.match(
      /^(\d{1,2}\.\d{1,2}\.\d{2,4})\s*-\s*(\d{1,2}\.\d{1,2}\.\d{2,4})?$/
    )
    if (m) {
      const from = parseDateInput(m[1])
      const to = m[2] ? parseDateInput(m[2]) : from
      if (from && to) return [from, to]
      return from ? [from, from] : null
    }

    // Одна дата (в т.ч. незавершённый ввод первой границы)
    return parseDateInput(s)
  } catch {
    return null
  }
}

/**
 * Форматирует дату к виду "dd.MM.yyyy" для отображения в поле.
 * @param {Date|null} date
 * @returns {string}
 */
export function formatDateForInput(date) {
  if (date == null) return ''
  if (!(date instanceof Date) || !isValid(date)) return ''
  try {
    return format(date, DISPLAY_FORMAT)
  } catch {
    return ''
  }
}
